// ============================================================================
// OPERATION PROGRESS CONTEXT - Context global per monitoritzar operacions
// Permet que qualsevol component mostri i gestioni el progrés d'operacions
// ============================================================================

import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import signalRService, { ProgressUpdate, OperationCompleted, OperationError } from '../services/signalr';
import * as signalR from '@microsoft/signalr';
import { OperationProgressBar, ActiveOperation } from '../components/OperationProgressBar';
import { useShop } from './ShopContext';

// Re-export per comoditat
export type { ActiveOperation };

interface ToastState {
  content: string;
  error?: boolean;
}

interface OperationProgressContextType {
  isConnected: boolean;
  connectionState: signalR.HubConnectionState;
  activeOperations: ActiveOperation[];
  startOperation: (correlationId: string, operationType: string, message?: string) => void;
  clearCompletedOperations: () => void;
  dismissOperation: (correlationId: string) => void;
  hasActiveOperations: boolean;
  showToast: (message: string, isError?: boolean) => void;
  toast: ToastState | null;
  dismissToast: () => void;
}

const OperationProgressContext = createContext<OperationProgressContextType | undefined>(undefined);

// Translate operation type to display name using i18n
const getOperationDisplayName = (t: (key: string, options?: Record<string, string>) => string, operationType: string): string => {
  const key = `operations.names.${operationType}`;
  const translated = t(key);
  // If translation key not found, i18next returns the key itself
  return translated === key ? operationType : translated;
};

// Resolve a translated message from messageKey + messageParams, falling back to raw message
const resolveMessage = (
  t: (key: string, options?: Record<string, string | number>) => string,
  message: string,
  messageKey?: string,
  messageParams?: Record<string, string | number>
): string => {
  if (!messageKey) return message;
  const translated = t(messageKey, messageParams);
  // If the key isn't found, i18next returns the key itself — fall back to raw message
  return translated === messageKey ? message : translated;
};

export function OperationProgressProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { shop } = useShop();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<signalR.HubConnectionState>(
    signalR.HubConnectionState.Disconnected
  );
  const [activeOperations, setActiveOperations] = useState<ActiveOperation[]>([]);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastShownRef = useRef<Set<string>>(new Set());

  // Show toast
  const showToast = useCallback((message: string, isError = false) => {
    setToast({ content: message, error: isError });
  }, []);

  // Start tracking an operation (called when API returns correlationId)
  const startOperation = useCallback((correlationId: string, operationType: string, message?: string) => {
    console.log('[OperationProgress] startOperation called:', { correlationId, operationType, message });
    
    // Subscribe to operation-specific updates via SignalR
    signalRService.subscribeToOperation(correlationId).catch(err => {
      console.warn('[OperationProgress] Failed to subscribe to operation:', err);
    });
    
    setActiveOperations(prev => {
      const existing = prev.find(op => op.correlationId === correlationId);
      if (existing) return prev;

      const newOp = {
        correlationId,
        operationType,
        status: 'started' as const,
        percentComplete: 0,
        message: message || t('operations.status.starting', { operation: getOperationDisplayName(t, operationType) }),
        startTime: new Date(),
      };
      console.log('[OperationProgress] Adding new operation:', newOp);
      return [...prev, newOp];
    });

    // Show started toast
    if (!toastShownRef.current.has(`started:${correlationId}`)) {
      toastShownRef.current.add(`started:${correlationId}`);
      showToast(`🚀 ${t('operations.status.started', { operation: getOperationDisplayName(t, operationType) })}`);
    }

    // Safety timeout: if no progress received within 5 minutes, mark as error
    // This prevents operations from being stuck in "started" state forever
    // when SignalR/Functions infrastructure isn't working
    setTimeout(() => {
      setActiveOperations(prev => {
        const op = prev.find(o => o.correlationId === correlationId);
        if (op && (op.status === 'started' || op.status === 'progress') && op.percentComplete < 100) {
          console.warn('[OperationProgress] Operation timed out:', correlationId);
          return prev.map(o =>
            o.correlationId === correlationId
              ? { ...o, status: 'error' as const, message: t('operations.status.timeout', 'Operation timed out - check server logs'), endTime: new Date() }
              : o
          );
        }
        return prev;
      });
    }, 5 * 60 * 1000); // 5 minutes
  }, [showToast, t]);

  // Handle progress updates from SignalR
  const handleProgress = useCallback((update: ProgressUpdate) => {
    const translatedMessage = resolveMessage(t, update.message, update.messageKey, update.messageParams);
    
    setActiveOperations(prev => {
      const existing = prev.find(op => op.correlationId === update.correlationId);
      
      if (existing) {
        return prev.map(op => 
          op.correlationId === update.correlationId
            ? { ...op, ...update, message: translatedMessage, status: update.status as ActiveOperation['status'] }
            : op
        );
      } else {
        // New operation (not manually registered)
        return [...prev, {
          correlationId: update.correlationId,
          operationType: update.operationType,
          status: update.status as ActiveOperation['status'],
          percentComplete: update.percentComplete,
          message: translatedMessage,
          startTime: new Date(),
        }];
      }
    });
  }, [t]);

  // Handle completion
  const handleCompleted = useCallback((completed: OperationCompleted) => {
    const translatedMessage = resolveMessage(t, completed.message, completed.messageKey, completed.messageParams);
    
    setActiveOperations(prev => 
      prev.map(op => 
        op.correlationId === completed.correlationId
          ? {
              ...op,
              status: 'completed' as const,
              percentComplete: 100,
              message: translatedMessage,
              endTime: new Date(),
              itemsProcessed: completed.itemsProcessed,
              durationMs: completed.durationMs,
            }
          : op
      )
    );

    // Show completion toast
    if (!toastShownRef.current.has(`completed:${completed.correlationId}`)) {
      toastShownRef.current.add(`completed:${completed.correlationId}`);
      const duration = completed.durationMs ? ` (${(completed.durationMs / 1000).toFixed(1)}s)` : '';
      showToast(`✅ ${t('operations.status.completed', { operation: getOperationDisplayName(t, completed.operationType) })}${duration}`);
    }

    // Auto-remove after 10 seconds
    setTimeout(() => {
      setActiveOperations(prev => 
        prev.filter(op => op.correlationId !== completed.correlationId)
      );
      toastShownRef.current.delete(`started:${completed.correlationId}`);
      toastShownRef.current.delete(`completed:${completed.correlationId}`);
    }, 10000);
  }, [showToast, t]);

  // Handle errors
  const handleError = useCallback((error: OperationError) => {
    setActiveOperations(prev => 
      prev.map(op => 
        op.correlationId === error.correlationId
          ? {
              ...op,
              status: 'error' as const,
              percentComplete: 0,
              message: error.errorMessage,
              error: error.errorMessage,
              endTime: new Date(),
            }
          : op
      )
    );

    showToast(`❌ Error: ${error.errorMessage}`, true);

    // Auto-remove after 30 seconds
    setTimeout(() => {
      setActiveOperations(prev => 
        prev.filter(op => op.correlationId !== error.correlationId)
      );
    }, 30000);
  }, [showToast]);

  // Handle connection state changes
  const handleConnectionChange = useCallback((state: signalR.HubConnectionState) => {
    setConnectionState(state);
    setIsConnected(state === signalR.HubConnectionState.Connected);
  }, []);

  // Connect to SignalR when shop is available
  useEffect(() => {
    if (!shop) return;

    // Subscribe to events
    const unsubProgress = signalRService.onProgress(handleProgress);
    const unsubCompleted = signalRService.onCompleted(handleCompleted);
    const unsubError = signalRService.onError(handleError);
    const unsubConnection = signalRService.onConnectionChange(handleConnectionChange);

    // Connect
    signalRService.connect(shop).catch(console.error);

    // Cleanup
    return () => {
      unsubProgress();
      unsubCompleted();
      unsubError();
      unsubConnection();
    };
  }, [shop, handleProgress, handleCompleted, handleError, handleConnectionChange]);

  // Clear completed operations
  const clearCompletedOperations = useCallback(() => {
    setActiveOperations(prev => 
      prev.filter(op => op.status !== 'completed' && op.status !== 'error')
    );
    toastShownRef.current.clear();
  }, []);

  // Dismiss operation
  const dismissOperation = useCallback((correlationId: string) => {
    setActiveOperations(prev => prev.filter(op => op.correlationId !== correlationId));
    toastShownRef.current.delete(`started:${correlationId}`);
    toastShownRef.current.delete(`completed:${correlationId}`);
  }, []);

  // Dismiss toast
  const dismissToast = useCallback(() => {
    setToast(null);
  }, []);

  const value: OperationProgressContextType = {
    isConnected,
    connectionState,
    activeOperations,
    startOperation,
    clearCompletedOperations,
    hasActiveOperations: activeOperations.some(op => 
      op.status === 'started' || op.status === 'progress'
    ),
    showToast,
    dismissOperation,
    toast,
    dismissToast,
  };

  return (
    <OperationProgressContext.Provider value={value}>
      {children}
      
      {/* Floating progress bar */}
      <OperationProgressBar
        operations={activeOperations}
        onDismiss={dismissOperation}
      />
    </OperationProgressContext.Provider>
  );
}

export function useOperationProgressContext() {
  const context = useContext(OperationProgressContext);
  if (!context) {
    throw new Error('useOperationProgressContext must be used within OperationProgressProvider');
  }
  return context;
}

export default OperationProgressContext;
